import React from 'react';
import { trackEvent } from './Matomo';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Mise à jour de l'état pour afficher l'UI de secours
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Logger l'erreur
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Tracker l'erreur dans Matomo
    trackEvent(
      'React Error Boundary',
      error.toString(),
      errorInfo.componentStack?.split('\n')[1]?.trim() || 'Unknown component'
    );

    // Mettre à jour l'état avec les détails de l'erreur
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Envoyer à un service de monitoring en production
    if (process.env.NODE_ENV === 'production') {
      // Ici vous pourriez envoyer à Sentry, LogRocket, etc.
      // this.logErrorToService(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    // Recharger la page si trop d'erreurs
    if (this.state.errorCount > 2) {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // UI de secours personnalisée
      return (
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.icon}>⚠️</div>
            <h1 style={styles.title}>Oups, quelque chose s'est mal passé</h1>
            <p style={styles.description}>
              Une erreur inattendue s'est produite. Nous en avons été informés et travaillons à la résoudre.
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={styles.details}>
                <summary style={styles.summary}>Détails de l'erreur (dev only)</summary>
                <pre style={styles.errorText}>
                  {this.state.error.toString()}
                  {'\n\n'}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div style={styles.actions}>
              <button onClick={this.handleReset} style={styles.button}>
                Réessayer
              </button>
              <button 
                onClick={() => window.location.href = '/'} 
                style={{...styles.button, ...styles.buttonSecondary}}
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Styles inline pour ErrorBoundary (pour éviter dépendance CSS)
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backgroundColor: '#f7fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  content: {
    maxWidth: '600px',
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '3rem 2rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    textAlign: 'center'
  },
  icon: {
    fontSize: '4rem',
    marginBottom: '1rem'
  },
  title: {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '1rem'
  },
  description: {
    fontSize: '1.125rem',
    color: '#546575',
    marginBottom: '2rem',
    lineHeight: '1.6'
  },
  details: {
    textAlign: 'left',
    marginBottom: '2rem',
    backgroundColor: '#f7fafc',
    padding: '1rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0'
  },
  summary: {
    cursor: 'pointer',
    fontWeight: '600',
    color: '#4a5568',
    marginBottom: '0.5rem'
  },
  errorText: {
    fontSize: '0.875rem',
    color: '#e53e3e',
    overflow: 'auto',
    maxHeight: '200px',
    marginTop: '1rem'
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  button: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    color: 'white',
    transition: 'transform 0.2s',
    minWidth: '150px'
  },
  buttonSecondary: {
    background: 'transparent',
    border: '2px solid #4c51bf',
    color: '#4c51bf'
  }
};

export default ErrorBoundary;



